import { StyleSheet } from '@react-pdf/renderer'

export const colors = {
  primary: '#000000',
  background: '#ffffff',
  border: '#999999',
  text: {
    primary: '#000000',
    secondary: '#666666'
  },
  tables: {
    header: '#f5f5f5',
    alternateRow: '#f8f8f8',
    aidStation: '#e8e8e8'
  },
  nutrition: {
    background: '#f8f9fa'
  },
  notes: {
    background: '#fff3cd',
    border: '#ffc107',
    text: '#856404'
  }
}

export const fonts = {
  compact: 8,
  small: 9,
  body: 11,
  medium: 12,
  large: 14,
  title: 20,
  heading: 24
}

export const spacing = {
  xs: 4,
  sm: 8,
  small: 8,
  md: 12,
  medium: 12,
  lg: 16,
  xl: 20
}

export const styles = StyleSheet.create({
  /* 
   * PRINT MARGIN CONSIDERATIONS:
   * 
   * Full Plan (A4): 35mm left/right, 40mm top/bottom
   * - Accounts for standard printer margins (6-13mm)
   * - Safe zone for hole punching/binding
   * - Comfortable reading margins
   * 
   * Compact Card: 18mm left/right, 20mm top/bottom  
   * - Minimum safe zone for cutting
   * - Accounts for printer limitations
   * - Optimized for small card printing
   */

  // Page and layout styles
  page: {
    flexDirection: 'column',
    backgroundColor: colors.background,
    paddingTop: 40,
    paddingBottom: 40,
    paddingLeft: 35,
    paddingRight: 35,
    fontFamily: 'Helvetica',
    fontSize: fonts.body,
    color: colors.text.primary
  },
  
  pageCompact: {
    flexDirection: 'column',
    backgroundColor: colors.background,
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 18,
    paddingRight: 18,
    fontFamily: 'Helvetica',
    fontSize: fonts.small,
    color: colors.text.primary
  },

  // Header styles
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xl,
    paddingBottom: spacing.md,
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
    borderBottomStyle: 'solid'
  },

  headerCompact: {
    textAlign: 'center',
    marginBottom: spacing.md,
    paddingBottom: spacing.xs
  },

  logo: {
    fontSize: fonts.heading,
    fontWeight: 'bold',
    color: colors.primary
  },

  runTitle: {
    fontSize: fonts.title,
    fontWeight: 'bold',
    textAlign: 'right',
    color: colors.primary
  },

  runTitleCompact: {
    fontSize: fonts.large,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: colors.primary
  },

  runDate: {
    fontSize: fonts.body,
    color: colors.text.secondary,
    textAlign: 'right',
    marginTop: 4
  },

  runDateCompact: {
    fontSize: fonts.small,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    marginTop: spacing.xs
  },

  // Summary section styles
  summarySection: {
    marginBottom: spacing.xl
  },

  summaryStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.tables.header,
    borderRadius: 4
  },

  statItem: {
    flex: 1,
    textAlign: 'center'
  },

  statLabel: {
    fontSize: fonts.small,
    textTransform: 'uppercase',
    color: colors.text.secondary,
    marginBottom: 4
  },

  statValue: {
    fontSize: fonts.medium,
    fontWeight: 'bold',
    color: colors.primary
  },

  nutritionSummary: {
    marginTop: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.nutrition.background,
    borderRadius: 4
  },

  nutritionTitle: {
    fontSize: fonts.medium,
    fontWeight: 'bold',
    marginBottom: spacing.sm,
    color: colors.primary
  },

  nutritionStats: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },

  // Table styles (print-optimized)
  table: {
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1.2, // Slightly thicker for better print visibility
    borderColor: colors.border,
    marginBottom: spacing.lg
  },

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.tables.header,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    borderBottomStyle: 'solid'
  },

  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    borderBottomStyle: 'solid',
    minHeight: 25
  },

  tableRowAlternate: {
    flexDirection: 'row',
    backgroundColor: colors.tables.alternateRow,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    borderBottomStyle: 'solid',
    minHeight: 25
  },

  tableRowAidStation: {
    flexDirection: 'row',
    backgroundColor: colors.tables.aidStation,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    borderBottomStyle: 'solid',
    minHeight: 25
  },

  tableCell: {
    padding: 6,
    fontSize: fonts.small,
    textAlign: 'left',
    alignItems: 'center',
    justifyContent: 'center',
    borderRightWidth: 1,
    borderRightColor: colors.border,
    borderRightStyle: 'solid'
  },

  tableCellHeader: {
    padding: 6,
    fontSize: fonts.small,
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
    borderRightWidth: 1,
    borderRightColor: colors.border,
    borderRightStyle: 'solid'
  },

  tableCellLast: {
    borderRightWidth: 0
  },

  // Compact table - simplified
  compactTable: {
    width: '100%',
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'solid'
  },

  compactTableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary
  },

  compactTableHeaderCell: {
    padding: 6,
    fontSize: fonts.compact,
    fontWeight: 'bold',
    color: colors.background,
    textAlign: 'center',
    borderRightWidth: 1,
    borderRightColor: colors.background,
    borderRightStyle: 'solid'
  },

  compactTableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    borderBottomStyle: 'solid',
    minHeight: 20
  },

  compactTableCell: {
    padding: 4,
    fontSize: fonts.compact,
    textAlign: 'center',
    borderRightWidth: 1,
    borderRightColor: colors.border,
    borderRightStyle: 'solid'
  },

  compactTableCellLast: {
    borderRightWidth: 0
  },

  // Text styles
  sectionTitle: {
    fontSize: fonts.large,
    fontWeight: 'bold',
    marginBottom: spacing.md,
    color: colors.primary
  },

  textBold: {
    fontWeight: 'bold'
  },

  textSecondary: {
    color: colors.text.secondary
  },

  textSmall: {
    fontSize: fonts.small
  },

  textLarge: {
    fontSize: fonts.large
  },

  textCenter: {
    textAlign: 'center'
  },

  textRight: {
    textAlign: 'right'
  },


  notes: {
    marginTop: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.notes.background,
    borderLeftWidth: 4,
    borderLeftColor: colors.notes.border,
    borderLeftStyle: 'solid'
  },

  notesTitle: {
    fontSize: fonts.medium,
    fontWeight: 'bold',
    color: colors.notes.text,
    marginBottom: spacing.xs
  },

  notesText: {
    fontSize: fonts.body,
    color: colors.notes.text,
    lineHeight: 1.4
  },

  // Footer styles
  footer: {
    marginTop: 'auto',
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    borderTopStyle: 'solid',
    fontSize: fonts.small,
    color: colors.text.secondary,
    textAlign: 'center'
  },

  footerCompact: {
    marginTop: spacing.md,
    textAlign: 'center'
  },

  // Compact header
  compactHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.medium,
    paddingBottom: spacing.small,
    borderBottomWidth: 1,
    borderBottomColor: colors.primary,
    borderBottomStyle: 'solid'
  },

  compactHeaderCell: {
    flex: 1,
    alignItems: 'center'
  },

  compactHeaderText: {
    fontSize: fonts.compact,
    fontWeight: 'bold',
    color: colors.text.primary,
    textAlign: 'center'
  },

  // Compact notes
  compactNotes: {
    marginTop: spacing.medium,
    padding: spacing.small,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    borderTopStyle: 'solid'
  },

  compactNotesText: {
    fontSize: fonts.compact,
    color: colors.text.primary
  },

  // Utility styles
  flexRow: {
    flexDirection: 'row'
  },

  spaceBetween: {
    justifyContent: 'space-between'
  },

  alignCenter: {
    alignItems: 'center'
  },

  marginBottom: {
    marginBottom: spacing.md
  },

  marginBottomLarge: {
    marginBottom: spacing.lg
  }
})

// Column width presets for different table layouts
export const columnWidths = {
  fullPlan: {
    '#': '5%',
    'Segment': '20%',
    'Dist (km)': '8%',
    'D+ (m)': '8%',
    'D- (m)': '8%',
    'Net Elev': '8%',
    'Pace (min/km)': '8%',
    'Time': '10%',
    'Cum Dist': '8%',
    'ETA': '10%',
    'Carbs': '7%',
    'Fluids': '7%'
  },
  
  compactCard: {
    'Seg': '25%',
    'Km': '15%',
    'ETA (T+)': '25%',
    'Nutrition': '35%'
  }
}